import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Login from "@/components/Login";
import HomeTab from "@/components/HomeTab";
import MapTab from "@/components/MapTab";
import BookingsTab from "@/components/BookingsTab";
import ProfileTab from "@/components/ProfileTab";
import ContactTab from "@/components/ContactTab";
import StudioDetailDialog from "@/components/StudioDetailDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AudioWaveform, Search, MapPin, CalendarDays, User, LogOut, Mail } from "lucide-react";

export default function MainApp() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState("home");
  const [selected, setSelected] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  useEffect(() => { if (user) setShowLogin(false); }, [user]);

  const tabs = [
    { id: "home", label: t("tab_home"), icon: Search },
    { id: "map", label: t("tab_map"), icon: MapPin },
    { id: "bookings", label: t("tab_bookings"), icon: CalendarDays },
    { id: "profile", label: t("tab_profile"), icon: User },
    { id: "contact", label: t("tab_contact"), icon: Mail },
  ];

  if (showLogin && !user) return <Login onClose={() => setShowLogin(false)} />;

  return (
    <div className="min-h-screen pb-24 lg:pb-10">
      {/* Top navbar */}
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center shadow-lg shadow-primary/40">
              <AudioWaveform className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-lg font-extrabold tracking-tight hidden sm:block">StudioSync</span>
          </div>

          {/* Desktop tabs */}
          <nav className="hidden lg:flex items-center gap-1">
            {tabs.map((tb) => (
              <button
                key={tb.id}
                data-testid={`tab-${tb.id}-desktop`}
                onClick={() => setTab(tb.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  tab === tb.id ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tb.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="outline-none">
                <Avatar className="w-9 h-9 border border-border">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-secondary text-xs">{user?.name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  <span className="inline-block mt-1 text-[10px] uppercase tracking-wider text-primary font-semibold">
                    {user?.is_admin ? "Admin" : t(user?.role || "musician")}
                  </span>
                </div>
                <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            ) : (
              <Button data-testid="header-login-btn" onClick={() => setShowLogin(true)} size="sm" className="rounded-full bg-primary hover:bg-primary/90 font-semibold">
                {t("register")}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {tab === "home" && <HomeTab onOpen={setSelected} />}
        {tab === "map" && <MapTab onOpen={setSelected} />}
        {tab === "bookings" && (user ? <BookingsTab /> : (
          <div className="text-center py-20 border border-dashed border-border rounded-2xl">
            <p className="text-muted-foreground mb-4">{t("auth_prompt")}</p>
            <Button data-testid="prompt-signup-bookings" onClick={() => setShowLogin(true)} className="rounded-xl bg-primary hover:bg-primary/90 font-semibold">{t("register")}</Button>
          </div>
        ))}
        {tab === "profile" && (user ? <ProfileTab /> : (
          <div className="text-center py-20 border border-dashed border-border rounded-2xl">
            <p className="text-muted-foreground mb-4">{t("auth_prompt")}</p>
            <Button data-testid="prompt-signup-profile" onClick={() => setShowLogin(true)} className="rounded-xl bg-primary hover:bg-primary/90 font-semibold">{t("register")}</Button>
          </div>
        ))}
        {tab === "contact" && <ContactTab />}
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 glass border-t border-border">
        <div className="grid grid-cols-5 max-w-lg mx-auto">
          {tabs.map((tb) => {
            const Icon = tb.icon;
            const active = tab === tb.id;
            return (
              <button
                key={tb.id}
                data-testid={`tab-${tb.id}`}
                onClick={() => setTab(tb.id)}
                className={`flex flex-col items-center gap-1 py-3 transition-colors ${
                  active ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} />
                <span className="text-[10px] font-medium">{tb.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {selected && (
        <StudioDetailDialog studio={selected} open={!!selected} onClose={() => setSelected(null)} onRequireAuth={() => { setSelected(null); setShowLogin(true); }} />
      )}
    </div>
  );
}
